// ============================================================
// SHARED BADGES — 議事録/お知らせ/ナレッジ等で共通利用する小さな表示部品。
// NotesList の ProjectBadge / SourceBadge を共有化し、NoteDetail で使う
// StatusPill / Avatar / Chevron / MemberAvatarStack を追加する。
// モックの <Icon name="..."> 依存（lucide）は持ち込まず、必要な箇所だけ
// インラインSVG（chevron 等）にする＝既存 Vite 実装（Home/NotesList/TopBar）の流儀に揃える。
// 色・余白は tokens / Tailwind 拡張（amber-deep / amber-soft / sky-soft 等）準拠。
// ============================================================
import { PROJECT_MAP, MEMBER_MAP } from '../data/demoData';
import { SOURCE_META, STATUS_META } from '../data/constants';
import type { ActionStatus, MeetingNote } from '../types/models';

/** PJバッジ（色ドット＋PJ名）。size='md' は議事録詳細ヘッダー用にやや大きめ。 */
export function ProjectBadge({ projectId, size }: { projectId: string; size?: 'sm' | 'md' }) {
  const p = PROJECT_MAP[projectId];
  if (!p) return null;
  const md = size === 'md';
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${
        md ? 'gap-2 px-3 py-1 text-xs' : 'gap-1.5 px-2.5 py-0.5 text-xs'
      }`}
      style={{ background: '#F1F5F9', color: '#475569' }}
    >
      <span className={`rounded-full ${md ? 'w-2.5 h-2.5' : 'w-2 h-2'}`} style={{ background: p.color }} />
      {p.name}
    </span>
  );
}

/** 議事録ソース（Google Meet / Notta / 手動）バッジ。 */
export function SourceBadge({ source }: { source: MeetingNote['source'] }) {
  const meta = SOURCE_META[source];
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-medium"
      style={{ background: meta.bg, color: meta.fg }}
    >
      {meta.label}
    </span>
  );
}

/** 議事録アクションの3値ステータス（未対応/対応中/完了）ピル。 */
export function StatusPill({ status }: { status: ActionStatus }) {
  const meta = STATUS_META[status] || STATUS_META.todo;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-2xs font-medium"
      style={{ background: meta.bg, color: meta.fg }}
    >
      {meta.label}
    </span>
  );
}

/** メンバーのイニシャル丸アバター。ring で重なり表示時の白縁を付ける。 */
export function Avatar({
  memberId,
  size = 26,
  ring,
}: {
  memberId: string;
  size?: number;
  ring?: boolean;
}) {
  const m = MEMBER_MAP[memberId];
  const label = m?.initials || memberId.slice(0, 1);
  return (
    <span
      className={`grid place-items-center rounded-full bg-charcoal text-white font-bold shrink-0${
        ring ? ' ring-2 ring-white' : ''
      }`}
      style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.42)) }}
      title={m?.name || memberId}
    >
      {label}
    </span>
  );
}

/** 参加者の重なりアバター列。 */
export function AvatarStack({ memberIds, size = 28 }: { memberIds: string[]; size?: number }) {
  return (
    <div className="flex -space-x-2">
      {memberIds.map((id) => (
        <Avatar key={id} memberId={id} size={size} ring />
      ))}
    </div>
  );
}

/** 開閉トグル等で使う chevron SVG（lucide 非依存）。 */
export function Chevron({
  dir,
  size = 16,
  className = '',
}: {
  dir: 'up' | 'down' | 'right';
  size?: number;
  className?: string;
}) {
  const d = dir === 'up' ? 'm18 15-6-6-6 6' : dir === 'right' ? 'm9 18 6-6-6-6' : 'm6 9 6 6 6-6';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
